import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router";
import Page from "components/page/Page";

import { StatusCode } from "api/protocols";
import Input from "components/form/input/Input";
import Card from "components/card/Card";
import Sheet from "components/sheet/Sheet";
import { IProduct, IVariant } from "redux/reducers/models/product";
import Toggle from "components/form/toggle/Toggle";
import { VariantCards } from "components/variantCards/VariantCards";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-light-svg-icons";
import { isEqual } from "lodash";
import { IUIActions } from "redux/actions/ui";
import { useTranslation } from "react-i18next";
import Popup from "components/popup/Popup";
import ProductSelect from "components/productModal/ProductSelect";
import {
  DeleteModifierGroup,
  GetModifierGroup,
  PostModifierGroupVariants,
  PutModifierGroup,
} from "api/rpc/2024-04/facilityAdmin/product/modifier/modifierGroup";
import { PutProductColor } from "api/rpc/2024-04/facilityAdmin/product/product";

interface IModifierGroupEdit {
  uiActions: IUIActions;
}

interface IModifierGroupEditable {
  id: number;
  title: string;
  multiSelect: boolean;
  required: boolean;
}

interface IModiferGroupState {
  modifierGroup: IModifierGroupEditable;
  modifierGroupLoaded: boolean;
}

interface IVariantState {
  variants: IVariant[];
  variantsLoaded: boolean;
  variantsToAddToModifierGroup: IVariant[];
  availableVariantPositions: number[];
}

interface IUnsavedChangesState {
  modifierGroupBeforeChanges: IModifierGroupEditable;
  variantsBeforeChanges: IVariant[];
}

interface IModalVisibilityState {
  deleteModalVisibility: boolean;
  addVariantsVisibility: boolean;
}

interface IFilterState {
  onlyModifierProducts: boolean;
}

export interface IProductOverride {
  id: number;
  button_color: string;
}

export interface IVariantModifier {
  id?: number;
  title?: string;
  default?: boolean;
  position?: number;
}

export default function ModifierGroupEdit(props: IModifierGroupEdit) {
  const { uiActions } = props;

  const history = useHistory();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const [modifierGroupState, setModifierGroupState] = useState<IModiferGroupState>({
    modifierGroup: {
      id: null,
      title: "",
      multiSelect: false,
      required: false,
    },
    modifierGroupLoaded: false,
  });

  const [variantState, setVariantState] = useState<IVariantState>({
    variants: [],
    variantsLoaded: false,
    variantsToAddToModifierGroup: [],
    availableVariantPositions: [],
  });

  const [unsavedChangesState, setUnsavedChangesState] = useState<IUnsavedChangesState>({
    modifierGroupBeforeChanges: undefined,
    variantsBeforeChanges: undefined,
  });

  const [modalVisibilityState, setModalVisibilityState] = useState<IModalVisibilityState>({
    deleteModalVisibility: false,
    addVariantsVisibility: false,
  });

  const [filterState, setFilterState] = useState<IFilterState>({
    onlyModifierProducts: true,
  });

  const [productOverrides, setProductOverrides] = useState<IProductOverride[]>([]);

  useEffect(() => {
    void loadModifierGroup();
  }, []);

  async function loadModifierGroup() {
    const resModGroup = await GetModifierGroup({ id: Number(id), extended: true }, true);
    if (resModGroup.status !== StatusCode.OK) {
      uiActions.showError(t("Failed to load modifier group"));
      return;
    }

    setModifierGroupState(prev => ({
      ...prev,
      modifierGroup: {
        id: resModGroup.data[0].id,
        title: resModGroup.data[0].title,
        multiSelect: resModGroup.data[0].multi_select,
        required: resModGroup.data[0].required,
      },
      modifierGroupLoaded: true,
    }));

    setVariantState(prev => ({
      ...prev,
      variants: resModGroup.data[0].variants,
      variantsLoaded: true,
    }));
  }

  async function removeModifierGroup() {
    const res = await DeleteModifierGroup({ id: modifierGroupState.modifierGroup.id }, true);

    if (res.status !== StatusCode.OK) {
      uiActions.showError(t("Failed to delete modifier group"));
      return;
    }

    history.push("/admin/settings/modifier_group");
  }

  function handleDeleteModalVisibility() {
    setModalVisibilityState(prev => ({
      ...prev,
      deleteModalVisibility: !modalVisibilityState.deleteModalVisibility,
    }));
  }

  function handleTitleChange(event: React.ChangeEvent<HTMLInputElement>) {
    setModifierGroupState(prev => ({ ...prev, modifierGroup: { ...prev.modifierGroup, title: event.target.value } }));
  }

  function handleMultiSelectToggle() {
    setModifierGroupState(prev => ({
      ...prev,
      modifierGroup: {
        ...prev.modifierGroup,
        multiSelect: !prev.modifierGroup.multiSelect,
      },
    }));
  }

  function handleRequiredToggle() {
    setModifierGroupState(prev => ({
      ...prev,
      modifierGroup: { ...prev.modifierGroup, required: !prev.modifierGroup.required },
    }));
  }

  function handleChangeVariantPosition(id: number, position: number) {
    setVariantState(prev => ({
      ...prev,
      variants: prev.variants.map(variant => {
        if (variant.id === id) {
          return {
            ...variant,
            modifier_group: {
              ...variant.modifier_group,
              position,
            },
          };
        } else {
          return variant;
        }
      }),
    }));
  }

  function handleRemoveVariant(id: number) {
    setVariantState(prev => ({
      ...prev,
      variants: prev.variants.filter(variant => variant.id !== id),
    }));
  }

  function handleOpenAddVariantsModal(availableVariantPositions: number[]) {
    setModalVisibilityState(prev => ({
      ...prev,
      addVariantsVisibility: !prev.addVariantsVisibility,
    }));
    setVariantState(prev => ({ ...prev, availableVariantPositions }));
    setFilterState({ onlyModifierProducts: true });
  }

  function handleCloseAddVariantsModal() {
    setModalVisibilityState(prev => ({
      ...prev,
      addVariantsVisibility: !prev.addVariantsVisibility,
    }));
    setVariantState(prev => ({
      ...prev,
      variantsToAddToModifierGroup: [],
      availableVariantPositions: [],
    }));
    setFilterState({ onlyModifierProducts: true });
  }

  function addVariants() {
    const updatedVariantsToAddToModifierGroup: IVariant[] = [];

    for (
      let i = 0;
      i < Math.min(variantState.variantsToAddToModifierGroup.length, variantState.availableVariantPositions.length);
      i++
    ) {
      const variantToAdd = variantState.variantsToAddToModifierGroup[i];

      updatedVariantsToAddToModifierGroup.push({
        ...variantToAdd,
        modifier_group: {
          ...variantToAdd.modifier_group,
          position: variantState.availableVariantPositions[i],
          default: false,
        },
      });
    }

    setVariantState(prev => ({
      ...prev,
      variants: [...prev.variants, ...updatedVariantsToAddToModifierGroup],
    }));
    handleCloseAddVariantsModal();
  }

  function handleProductSelect(products: IProduct[]) {
    const alreadyInGroup = new Set(variantState.variants.map(variant => variant.id));
    const selectedVariants: IVariant[] = [];

    products.forEach(product => {
      (product.variants ?? []).forEach(variant => {
        if (alreadyInGroup.has(variant.id)) {
          return;
        }

        selectedVariants.push({ ...variant, product });
      });
    });

    setVariantState(prev => ({
      ...prev,
      variantsToAddToModifierGroup: selectedVariants.slice(0, prev.availableVariantPositions.length),
    }));
  }

  function handleUnselectVariantToAdd(variant: IVariant) {
    setVariantState(prev => ({
      ...prev,
      variantsToAddToModifierGroup: prev.variantsToAddToModifierGroup.filter(variantToAdd => {
        return variantToAdd.id !== variant.id;
      }),
    }));
  }

  function selectedProductsFromVariants(variants: IVariant[]): IProduct[] {
    const productsById = new Map<number, IProduct>();

    variants.forEach(variant => {
      const product = variant.product;
      if (!product?.id) {
        return;
      }

      const existing = productsById.get(product.id);
      if (existing) {
        existing.variants = [...(existing.variants ?? []), variant];
        return;
      }

      productsById.set(product.id, { ...product, variants: [variant] });
    });

    return Array.from(productsById.values());
  }

  function setVariants(variants: IVariant[]) {
    setVariantState(prev => ({ ...prev, variants }));
  }

  function variantToVariantModifier(variant: IVariant): IVariantModifier {
    return {
      id: variant.id,
      title: variant.title,
      default: !!variant.modifier_group?.default,
      position: variant.modifier_group?.position,
    };
  }

  function unsavedChangesExist() {
    if (
      unsavedChangesState.modifierGroupBeforeChanges === undefined ||
      unsavedChangesState.variantsBeforeChanges === undefined
    ) {
      if (modifierGroupState.modifierGroupLoaded && variantState.variantsLoaded) {
        setUnsavedChangesState(prev => ({
          ...prev,
          modifierGroupBeforeChanges: modifierGroupState.modifierGroup,
          variantsBeforeChanges: variantState.variants,
        }));
      }
      return false;
    }

    const sortedVariantModifiersBeforeChanges = [...unsavedChangesState.variantsBeforeChanges]
      .sort((prev, next) => prev.id - next.id)
      .map(variantToVariantModifier);

    const sortedVariantModifiersAfterChanges = [...variantState.variants]
      .sort((prev, next) => prev.id - next.id)
      .map(variantToVariantModifier);

    return (
      !isEqual(unsavedChangesState.modifierGroupBeforeChanges, modifierGroupState.modifierGroup) ||
      !isEqual(sortedVariantModifiersBeforeChanges, sortedVariantModifiersAfterChanges) ||
      productOverrides.length > 0
    );
  }

  async function saveModifierGroupHandler() {
    uiActions.enqueue();

    const putModifierGroupResponse = await PutModifierGroup(
      {
        id: modifierGroupState.modifierGroup.id,
        title: modifierGroupState.modifierGroup.title,
        multi_select: modifierGroupState.modifierGroup.multiSelect,
        required: modifierGroupState.modifierGroup.required,
      },
      false,
    );

    if (putModifierGroupResponse.status !== StatusCode.OK) {
      uiActions.showError(t("Failed to update modifier group"));
      uiActions.dequeue();
      return;
    }

    const postModifierGroupVariantsResponse = await PostModifierGroupVariants(
      {
        modifier_group_id: modifierGroupState.modifierGroup.id,
        variants: variantState.variants.map(variantToVariantModifier),
      },
      false,
    );

    if (postModifierGroupVariantsResponse.status !== StatusCode.OK) {
      uiActions.showError(t("Failed to update modifier group variants"));
      uiActions.dequeue();
      return;
    }

    const putProductColorResponse = await PutProductColor(
      {
        products: productOverrides.map(productOverride => ({
          id: productOverride.id,
          button_color: productOverride.button_color,
        })),
      },
      false,
    );

    if (putProductColorResponse.status !== StatusCode.OK) {
      uiActions.showError(t("Failed to update variant colors"));
      uiActions.dequeue();
      return;
    }

    const resModGroup = await GetModifierGroup({ id: modifierGroupState.modifierGroup.id, extended: true }, false);

    if (resModGroup.status !== StatusCode.OK) {
      uiActions.showError(t("Failed to load updated modifier group"));
      uiActions.dequeue();
      return;
    }

    uiActions.dequeue();

    const updatedModifierGroup: IModifierGroupEditable = {
      id: resModGroup.data[0].id,
      title: resModGroup.data[0].title,
      multiSelect: resModGroup.data[0].multi_select,
      required: resModGroup.data[0].required,
    };

    const updatedVariants: IVariant[] = resModGroup.data[0].variants;

    setModifierGroupState(prev => ({
      ...prev,
      modifierGroup: updatedModifierGroup,
    }));

    setVariantState(prev => ({
      ...prev,
      variants: updatedVariants,
    }));

    setUnsavedChangesState(prev => ({
      ...prev,
      modifierGroupBeforeChanges: updatedModifierGroup,
      variantsBeforeChanges: updatedVariants,
    }));

    setProductOverrides([]);
  }

  function cancelUnsavedChanges() {
    setModifierGroupState(prev => ({
      ...prev,
      modifierGroup: unsavedChangesState.modifierGroupBeforeChanges,
    }));
    setVariantState(prev => ({
      ...prev,
      variants: unsavedChangesState.variantsBeforeChanges,
    }));
    setProductOverrides([]);
  }

  const selectedProducts = useMemo(
    () => selectedProductsFromVariants(variantState.variantsToAddToModifierGroup),
    [variantState.variantsToAddToModifierGroup],
  );

  const excludedVariantIds = useMemo(
    () => variantState.variants.map(variant => variant.id),
    [variantState.variants],
  );

  const getProductsParams = useMemo(
    () => (filterState.onlyModifierProducts ? { type: "Modifier" } : undefined),
    [filterState.onlyModifierProducts],
  );

  return (
    <div>
      <Page
        title={t("Edit Modifier Group: ") + (unsavedChangesState.modifierGroupBeforeChanges?.title ?? "")}
        narrow
        breadcrumbs={[
          {
            prefix: true,
            label: t("Modifier Groups"),
            url: "/admin/settings/modifier_group",
          },
        ]}
        notificationBarProps={{
          isVisible:
            unsavedChangesExist() &&
            !modalVisibilityState.addVariantsVisibility &&
            !modalVisibilityState.deleteModalVisibility,
          onAction: saveModifierGroupHandler,
          onCancel: cancelUnsavedChanges,
        }}
        multipleActionDropdownAction={{
          label: t("Options"),
          dropdownProps: {
            alignment: "right",
            options: [
              {
                type: "handler",
                label: t("Delete"),
                icon: "trash",
                handler: handleDeleteModalVisibility,
              },
              {
                type: "handler",
                label: t("Duplicate"),
                handler: handleDeleteModalVisibility,
                disabled: true,
                icon: "copy",
              },
            ],
          },
        }}
      >
        <Card>
          <Card.Section>
            <Input
              label={t("Title")}
              className="mb-4"
              value={modifierGroupState.modifierGroup.title}
              onChange={handleTitleChange}
              placeholder={t("Title")}
            />
            <div className="flex flex-column gap-3">
              <Toggle
                labelTop={t("Enable Multi Select")}
                checked={modifierGroupState.modifierGroup.multiSelect}
                onChange={handleMultiSelectToggle}
              />
              <Toggle
                labelTop={t("Forced Modifier")}
                checked={modifierGroupState.modifierGroup.required}
                onChange={handleRequiredToggle}
              />
            </div>
          </Card.Section>

          <Card.Section>
            <VariantCards
              variants={variantState.variants}
              variantsLoaded={variantState.variantsLoaded}
              onChangeVariantPosition={handleChangeVariantPosition}
              onRemoveVariant={handleRemoveVariant}
              onAddVariants={handleOpenAddVariantsModal}
              setVariants={setVariants}
              productOverrides={productOverrides}
              setProductOverrides={setProductOverrides}
            />
          </Card.Section>
        </Card>
      </Page>

      <Popup
        open={modalVisibilityState.deleteModalVisibility}
        type="warning"
        title={t("Delete Confirmation")}
        description={t("Are you sure you want to delete this modifier group? This action cannot be undone.")}
        okText={t("Delete")}
        onOk={removeModifierGroup}
        cancelText={t("Close")}
        onCancel={handleDeleteModalVisibility}
      />

      <Sheet
        closable
        onCancel={handleCloseAddVariantsModal}
        size="medium"
        open={modalVisibilityState.addVariantsVisibility}
        cancelText={t("Close")}
        okText={t("Add")}
        title={t("Add Variants")}
        onOk={addVariants}
      >
        <div className="mb-4">
          <div className="text-lg">
            {`${variantState.variantsToAddToModifierGroup.length} / ${variantState.availableVariantPositions.length} available positions filled`}
          </div>
          {variantState.variantsToAddToModifierGroup.length > 0 && (
            <div className="mt-3">
              {variantState.variantsToAddToModifierGroup.map(variant => {
                return (
                  <div
                    key={variant.id}
                    className="inline-flex p-2 whitespace-nowrap items-center rounded-lg mt-1 mb-1 mr-2 border"
                  >
                    <p className="whitespace-nowrap overflow-clip inline-block overflow-hidden ml-1 font-semibold">
                      {variant.product?.preferred_title ? variant.product.preferred_title : variant.product?.title}
                      {variant.product?.subtitle ? ` - ${variant.product.subtitle}` : ""}
                      {variant.title !== variant.product?.title ? ` - ${variant.title}` : ""}
                    </p>
                    <span>
                      <FontAwesomeIcon
                        onClick={() => handleUnselectVariantToAdd(variant)}
                        className="ml-2 cursor-pointer"
                        icon={faXmark}
                      />
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          <div className="mt-4 mb-3">
            <Toggle
              id="only-modifier-products"
              labelRight={t("Only show modifier products")}
              checked={filterState.onlyModifierProducts}
              onChange={event =>
                setFilterState(prevState => ({ ...prevState, onlyModifierProducts: event.target.checked }))
              }
            />
          </div>
        </div>

        {modalVisibilityState.addVariantsVisibility ? (
          <ProductSelect
            key={filterState.onlyModifierProducts ? "modifier-products" : "all-products"}
            selectedProducts={selectedProducts}
            onChange={handleProductSelect}
            search
            getProductsParams={getProductsParams}
            excludedVariantIds={excludedVariantIds}
            limit={50}
          />
        ) : null}
      </Sheet>
    </div>
  );
}
